<template>
  <div class="model-diff">
    <h2>Compare Versions</h2>
    <p class="hint">
      Admin only. Pick a model role, then choose a version on each side. Comparison is
      always within one role, since prompt keys differ between roles. The left side is the
      baseline (shown as <code>−</code>), the right side is the comparison (shown as
      <code>+</code>).
    </p>

    <!-- Role -->
    <div class="row">
      <div class="field">
        <label>Model role</label>
        <select v-model="modelGroup" :disabled="busy" @change="onRoleChange">
          <option v-for="mg in MODEL_GROUPS" :key="mg" :value="mg">{{ mg }}</option>
        </select>
      </div>
      <div class="field note-field">
        <p class="count-note" v-if="loaded">
          {{ models.length }} version(s) across {{ groupsWithVersions.length }} group(s)
        </p>
      </div>
    </div>

    <!-- Side pickers -->
    <div class="row sides">
      <div class="side">
        <h3 class="side-title base">Baseline (−)</h3>
        <div class="field">
          <label>Group</label>
          <select v-model="leftGroupId" :disabled="busy || !loaded" @change="onGroupChange('left')">
            <option :value="null" disabled>Select a group…</option>
            <option v-for="g in groupsWithVersions" :key="g.id" :value="g.id">{{ g.name }}</option>
          </select>
        </div>
        <div class="field">
          <label>Version</label>
          <select v-model="leftName" :disabled="busy || !leftVersions.length">
            <option v-if="!leftVersions.length" :value="''">No versions</option>
            <option v-for="v in leftVersions" :key="v.name" :value="v.name">
              {{ versionLabel(v) }}
            </option>
          </select>
        </div>
      </div>

      <div class="side">
        <h3 class="side-title compare">Comparison (+)</h3>
        <div class="field">
          <label>Group</label>
          <select v-model="rightGroupId" :disabled="busy || !loaded" @change="onGroupChange('right')">
            <option :value="null" disabled>Select a group…</option>
            <option v-for="g in groupsWithVersions" :key="g.id" :value="g.id">{{ g.name }}</option>
          </select>
        </div>
        <div class="field">
          <label>Version</label>
          <select v-model="rightName" :disabled="busy || !rightVersions.length">
            <option v-if="!rightVersions.length" :value="''">No versions</option>
            <option v-for="v in rightVersions" :key="v.name" :value="v.name">
              {{ versionLabel(v) }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Presets and view controls -->
    <div class="controls">
      <button class="btn secondary small" :disabled="busy || !previousOfRight" @click="selectPreviousOfRight">
        Baseline = previous version
      </button>
      <button class="btn secondary small" :disabled="busy || !canSwap" @click="swapSides">
        Swap sides
      </button>

      <span class="spacer"></span>

      <label class="toggle">
        <input type="checkbox" v-model="includeMeta" />
        Include metadata
      </label>
      <label class="toggle">
        <input type="checkbox" :checked="textMode === 'raw'" @change="textMode = $event.target.checked ? 'raw' : 'canonical'" />
        Raw JSON
      </label>
      <label class="toggle">
        <input type="checkbox" :checked="viewMode === 'split'" @change="viewMode = $event.target.checked ? 'split' : 'unified'" />
        Side by side
      </label>
    </div>

    <p v-if="!loaded && busy" class="status">Loading versions…</p>
    <p v-else-if="loaded && !models.length" class="status">
      No versions exist for role <code>{{ modelGroup }}</code>.
    </p>
    <p v-else-if="!leftModel || !rightModel" class="status">
      Select a version on both sides to see a diff.
    </p>
    <p v-else-if="sameSelection" class="status">
      Both sides point at <code>{{ leftName }}</code>. Pick a different version to compare.
    </p>

    <ModelDiffView
      v-else
      :left-text="leftText"
      :right-text="rightText"
      :left-label="leftLabel"
      :right-label="rightLabel"
      :mode="viewMode"
      empty-message="These two versions are identical for the fields being compared."
    />
  </div>
</template>

<script>
import modelApi from "../services/model_api";
import manageApi from "../services/manage_api";
import swal from "../services/swal";
import { FALLBACK_MODEL_GROUPS, versionNum } from "../utils/models";
import { toCanonicalText, toRawJsonText } from "../utils/model_diff";
import ModelDiffView from "./ModelDiffView.vue";

export default {
  name: "ModelDiffPanel",
  components: { ModelDiffView },
  props: {
    // The parent keeps this panel mounted so selections survive tab switches;
    // we only hit the API the first time it actually becomes visible.
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      // Replaced by GET /model/options on first open; see modelApi.loadOptions.
      MODEL_GROUPS: FALLBACK_MODEL_GROUPS,
      groups: [],
      models: [],
      modelGroup: "core",
      leftGroupId: null,
      leftName: "",
      rightGroupId: null,
      rightName: "",
      textMode: "canonical",
      viewMode: "split",
      includeMeta: true,
      loaded: false,
      busy: false,
    };
  },
  computed: {
    groupsWithVersions() {
      const ids = new Set(this.models.map((m) => m.group_id));
      return this.groups.filter((g) => ids.has(g.id));
    },
    leftVersions() {
      return this.versionsFor(this.leftGroupId);
    },
    rightVersions() {
      return this.versionsFor(this.rightGroupId);
    },
    leftModel() {
      return this.models.find((m) => m.name === this.leftName) || null;
    },
    rightModel() {
      return this.models.find((m) => m.name === this.rightName) || null;
    },
    sameSelection() {
      return !!this.leftName && this.leftName === this.rightName;
    },
    canSwap() {
      return !!this.leftModel && !!this.rightModel;
    },
    leftText() {
      return this.renderModel(this.leftModel);
    },
    rightText() {
      return this.renderModel(this.rightModel);
    },
    leftLabel() {
      return this.modelLabel(this.leftModel);
    },
    rightLabel() {
      return this.modelLabel(this.rightModel);
    },
    // The version immediately below the right-hand selection, in the same group.
    previousOfRight() {
      if (!this.rightModel) return null;
      const target = versionNum(this.rightModel.name);
      const candidates = this.versionsFor(this.rightModel.group_id).filter(
        (v) => versionNum(v.name) < target
      );
      return candidates.length ? candidates[0] : null;
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive && !this.loaded && !this.busy) this.load();
      },
    },
  },
  methods: {
    // Newest first, matching the editor panel's ordering.
    versionsFor(groupId) {
      if (groupId === null || groupId === undefined) return [];
      return this.models
        .filter((m) => m.group_id === groupId)
        .sort((a, b) => versionNum(b.name) - versionNum(a.name));
    },
    versionLabel(v) {
      return v.activated ? `${v.name} (active)` : v.name;
    },
    modelLabel(model) {
      if (!model) return "";
      const group = this.groups.find((g) => g.id === model.group_id);
      const suffix = model.activated ? " (active)" : "";
      return group ? `${group.name} / ${model.name}${suffix}` : `${model.name}${suffix}`;
    },
    renderModel(model) {
      if (!model) return "";
      const opts = { includeMeta: this.includeMeta };
      return this.textMode === "raw" ? toRawJsonText(model, opts) : toCanonicalText(model, opts);
    },
    async load() {
      this.busy = true;
      try {
        this.MODEL_GROUPS = (await modelApi.loadOptions()).modelGroups;
        if (!this.groups.length) {
          const groups = await manageApi.getGroups(true);
          this.groups = Array.isArray(groups) ? groups : [];
        }
        await this.refreshModels();
        this.loaded = true;
      } catch (e) {
        swal.fire({ icon: "error", title: "Could not load versions", text: String(e) });
      } finally {
        this.busy = false;
      }
    },
    async onRoleChange() {
      this.leftGroupId = null;
      this.rightGroupId = null;
      this.leftName = "";
      this.rightName = "";
      this.busy = true;
      try {
        await this.refreshModels();
      } catch (e) {
        swal.fire({ icon: "error", title: "Could not load versions", text: String(e) });
      } finally {
        this.busy = false;
      }
    },
    // One call with no group_id returns every version of this role across all
    // groups, prompts included, which is all the diff needs. No backend change.
    async refreshModels() {
      const models = await modelApi.listModels({ model_group: this.modelGroup });
      this.models = Array.isArray(models) ? models : [];
      this.applyDefaultSelection();
    },
    // Default to the most useful comparison: the active version of some group
    // on the right, and the version before it on the left.
    applyDefaultSelection() {
      if (!this.models.length) return;
      const active = this.models.find((m) => m.activated);
      const anchor = active || this.versionsFor(this.models[0].group_id)[0] || this.models[0];

      this.rightGroupId = anchor.group_id;
      this.rightName = anchor.name;

      const previous = this.previousOfRight;
      if (previous) {
        this.leftGroupId = previous.group_id;
        this.leftName = previous.name;
      } else {
        this.leftGroupId = anchor.group_id;
        this.leftName = "";
      }
    },
    onGroupChange(side) {
      const versions = this.versionsFor(side === "left" ? this.leftGroupId : this.rightGroupId);
      const preferred = versions.find((v) => v.activated) || versions[0] || null;
      const name = preferred ? preferred.name : "";
      if (side === "left") this.leftName = name;
      else this.rightName = name;
    },
    selectPreviousOfRight() {
      const previous = this.previousOfRight;
      if (!previous) return;
      this.leftGroupId = previous.group_id;
      this.leftName = previous.name;
    },
    swapSides() {
      const groupId = this.leftGroupId;
      const name = this.leftName;
      this.leftGroupId = this.rightGroupId;
      this.leftName = this.rightName;
      this.rightGroupId = groupId;
      this.rightName = name;
    },
  },
};
</script>

<style scoped>
.model-diff {
  padding: 20px;
  background-color: #ffffffb2;
  /* Top-left is square so the panel joins the tab strip above it. */
  border-radius: 0 20px 20px 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  /* Side inset comes from .config-shell so both tabs are exactly as wide. */
  margin: 0 0 20px;
  max-width: none;
  width: 100%;
  text-align: left;
}
.hint {
  font-size: 0.9em;
  color: #444;
  margin-bottom: 16px;
}
.hint code,
.status code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.row.sides {
  gap: 24px;
}
.side {
  flex: 1;
  min-width: 260px;
}
.side-title {
  font-size: 0.95em;
  margin: 0 0 10px;
  padding-bottom: 6px;
  border-bottom: 2px solid #e2e5e9;
}
.side-title.base {
  color: #a4262c;
  border-bottom-color: #f0c8cb;
}
.side-title.compare {
  color: #1a7f37;
  border-bottom-color: #bde5c8;
}
.field {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  flex: 1;
  min-width: 180px;
}
.note-field {
  justify-content: flex-end;
}
.count-note {
  margin: 0 0 8px;
  font-size: 0.85em;
  color: #555;
}
.field label {
  margin-bottom: 6px;
  font-weight: 600;
}
.field select {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.spacer {
  flex: 1;
}
.toggle {
  font-size: 0.88em;
  white-space: nowrap;
}
.status {
  margin: 0;
  padding: 14px;
  background: #f6f7f9;
  border: 1px solid #e2e5e9;
  border-radius: 6px;
  color: #555;
  font-size: 0.9em;
}
.btn {
  padding: 8px 14px;
  font-size: 14px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.btn.small {
  font-size: 13px;
  padding: 7px 12px;
}
.btn.secondary {
  background-color: #6c757d;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
